// @ts-nocheck
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { generateClient } from "aws-amplify/api";

const client = generateClient();

// Shared field selections (mirrors the schema's public read-model types).
const PUBLIC_PROFILE_FIELDS = /* GraphQL */ `
  id
  displayName
  email
  bio
  profilePictureUrl
  showDisplayName
  showEmail
  showBio
  showProfilePicture
  organizationProfileId
  createdAt
  updatedAt
`;

const ORG_PUBLIC_PROFILE_FIELDS = /* GraphQL */ `
  id
  displayName
  email
  bio
  profilePictureUrl
  showDisplayName
  showEmail
  showBio
  showProfilePicture
  createdAt
  updatedAt
`;

// --- User PublicProfile operations ---
const getPublicProfile = /* GraphQL */ `
  query GetPublicProfile($id: ID!) {
    getPublicProfile(id: $id) { ${PUBLIC_PROFILE_FIELDS} }
  }
`;

const listPublicProfiles = /* GraphQL */ `
  query ListPublicProfiles($limit: Int, $nextToken: String) {
    listPublicProfiles(limit: $limit, nextToken: $nextToken) {
      items { ${PUBLIC_PROFILE_FIELDS} }
      nextToken
    }
  }
`;

const createPublicProfile = /* GraphQL */ `
  mutation CreatePublicProfile($input: CreatePublicProfileInput!) {
    createPublicProfile(input: $input) { ${PUBLIC_PROFILE_FIELDS} }
  }
`;

const updatePublicProfile = /* GraphQL */ `
  mutation UpdatePublicProfile($input: UpdatePublicProfileInput!) {
    updatePublicProfile(input: $input) { ${PUBLIC_PROFILE_FIELDS} }
  }
`;

// --- OrganizationPublicProfile operations ---
const getOrganizationPublicProfile = /* GraphQL */ `
  query GetOrganizationPublicProfile($id: ID!) {
    getOrganizationPublicProfile(id: $id) { ${ORG_PUBLIC_PROFILE_FIELDS} }
  }
`;

const listOrganizationPublicProfiles = /* GraphQL */ `
  query ListOrganizationPublicProfiles($limit: Int, $nextToken: String) {
    listOrganizationPublicProfiles(limit: $limit, nextToken: $nextToken) {
      items { ${ORG_PUBLIC_PROFILE_FIELDS} }
      nextToken
    }
  }
`;

const createOrganizationPublicProfile = /* GraphQL */ `
  mutation CreateOrganizationPublicProfile($input: CreateOrganizationPublicProfileInput!) {
    createOrganizationPublicProfile(input: $input) { ${ORG_PUBLIC_PROFILE_FIELDS} }
  }
`;

const updateOrganizationPublicProfile = /* GraphQL */ `
  mutation UpdateOrganizationPublicProfile($input: UpdateOrganizationPublicProfileInput!) {
    updateOrganizationPublicProfile(input: $input) { ${ORG_PUBLIC_PROFILE_FIELDS} }
  }
`;

const initialState = {
  loading: "idle",
  error: null,
  myProfile: null,        // current user's PublicProfile (null when none exists)
  myProfileLoaded: false,
  orgProfiles: {},        // { [orgId]: OrganizationPublicProfile }
  allProfiles: [],        // directory: all PublicProfiles
  allOrgProfiles: [],     // directory: all OrganizationPublicProfiles
};

// Fetch the signed-in user's own public profile by id (= userId). Returns null when absent.
export const fetchMyPublicProfile = createAsyncThunk(
  "publicProfile/fetchMine",
  async (userId, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: getPublicProfile,
        variables: { id: userId },
      });
      return data.getPublicProfile;
    } catch (error) {
      console.error("Error fetching public profile:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const createMyPublicProfile = createAsyncThunk(
  "publicProfile/create",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: createPublicProfile,
        variables: { input },
      });
      return data.createPublicProfile;
    } catch (error) {
      console.error("Error creating public profile:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateMyPublicProfile = createAsyncThunk(
  "publicProfile/update",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: updatePublicProfile,
        variables: { input },
      });
      return data.updatePublicProfile;
    } catch (error) {
      console.error("Error updating public profile:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchOrganizationPublicProfile = createAsyncThunk(
  "publicProfile/fetchOrg",
  async (organizationId, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: getOrganizationPublicProfile,
        variables: { id: organizationId },
      });
      return { organizationId, profile: data.getOrganizationPublicProfile };
    } catch (error) {
      console.error("Error fetching org public profile:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const createOrgPublicProfile = createAsyncThunk(
  "publicProfile/createOrg",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: createOrganizationPublicProfile,
        variables: { input },
      });
      return data.createOrganizationPublicProfile;
    } catch (error) {
      console.error("Error creating org public profile:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateOrgPublicProfile = createAsyncThunk(
  "publicProfile/updateOrg",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: updateOrganizationPublicProfile,
        variables: { input },
      });
      return data.updateOrganizationPublicProfile;
    } catch (error) {
      console.error("Error updating org public profile:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Directory page: list every public profile (users + orgs).
export const fetchAllPublicProfiles = createAsyncThunk(
  "publicProfile/fetchAll",
  async (_, thunkAPI) => {
    try {
      const [users, orgs] = await Promise.all([
        client.graphql({ query: listPublicProfiles, variables: { limit: 1000 } }),
        client.graphql({ query: listOrganizationPublicProfiles, variables: { limit: 1000 } }),
      ]);
      // Single-page fetch (this is an admin verify page). Flag if we've truncated so it's not silent.
      if (users.data?.listPublicProfiles?.nextToken || orgs.data?.listOrganizationPublicProfiles?.nextToken) {
        console.warn("fetchAllPublicProfiles: more than 1000 profiles exist — directory list is truncated.");
      }
      return {
        users: users.data?.listPublicProfiles?.items || [],
        orgs: orgs.data?.listOrganizationPublicProfiles?.items || [],
      };
    } catch (error) {
      console.error("Error fetching all public profiles:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const publicProfileSlice = createSlice({
  name: "publicProfile",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyPublicProfile.fulfilled, (state, action) => {
        state.myProfile = action.payload;
        state.myProfileLoaded = true;
      })
      .addCase(createMyPublicProfile.fulfilled, (state, action) => {
        state.myProfile = action.payload;
        state.myProfileLoaded = true;
      })
      .addCase(updateMyPublicProfile.fulfilled, (state, action) => {
        state.myProfile = action.payload;
      })
      .addCase(fetchOrganizationPublicProfile.fulfilled, (state, action) => {
        state.orgProfiles[action.payload.organizationId] = action.payload.profile;
      })
      .addCase(createOrgPublicProfile.fulfilled, (state, action) => {
        state.orgProfiles[action.payload.id] = action.payload;
      })
      .addCase(updateOrgPublicProfile.fulfilled, (state, action) => {
        state.orgProfiles[action.payload.id] = action.payload;
      })
      .addCase(fetchAllPublicProfiles.fulfilled, (state, action) => {
        state.allProfiles = action.payload.users;
        state.allOrgProfiles = action.payload.orgs;
      });
  },
});

export default publicProfileSlice.reducer;
